import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import Header from '../../../shared/components/layout/Header';
import Layout from '../../components/layout/AppLayout';
import FormInput from '../../../shared/components/ui/FormInput';
import SalesUnitInventoryList from './SalesUnitInventoryList';
import Loader from '../../../shared/components/ui/Loader';
import { getAllBlocks } from '../../services/api/implementation/impl';

const SalesUnitInventory = ({
  embedded = false,
  forcedProjectID = null,
  openBulkUnitUpdate = false
}) => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { projectsForDropdown, isProjectsLoading } = useProjectsList();
  const { collapsed } = useSidebar();
  const {
    register,
    setValue,
    watch,
    formState: { errors }
  } = useForm();

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [bulkUpdatePortalEl, setBulkUpdatePortalEl] = useState(null);
  const bulkUpdatePortalRef = useCallback((node) => {
    setBulkUpdatePortalEl(node);
  }, []);

  const projectFieldValue = watch('project');
  const blockFieldValue = watch('block');

  const effectiveProjectId = embedded
    ? selectedProjectId
    : routeId
      ? parseInt(String(routeId), 10)
      : null;

  const { data: blocksDyna, isLoading: isBlocksLoading, isFetching: isFetchingBlocks } = useQuery({
    queryKey: ['blocks', effectiveProjectId],
    queryFn: () => getAllBlocks(effectiveProjectId),
    enabled: Number.isFinite(effectiveProjectId) && effectiveProjectId > 0
  });

  const isPageLoading =
    isProjectsLoading ||
    (Number.isFinite(effectiveProjectId) &&
      effectiveProjectId > 0 &&
      (isBlocksLoading || isFetchingBlocks));

  const blockOptions = useMemo(() => {
    const allBlocksOption = { label: 'All Blocks', value: 'all' };
    if (!Array.isArray(blocksDyna) || blocksDyna.length === 0) return [allBlocksOption];
    const withLevels = blocksDyna.filter((b) => b?.levels?.length > 0);
    const sorted = withLevels.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return [allBlocksOption, ...sorted.map((block) => ({ label: block.name, value: String(block.id) }))];
  }, [blocksDyna]);

  useEffect(() => {
    if (!projectsForDropdown?.length) return;
    if (embedded) {
      if (forcedProjectID != null && forcedProjectID !== '') {
        const n = parseInt(String(forcedProjectID), 10);
        if (!Number.isNaN(n) && n > 0) {
          setValue('project', n);
          setSelectedProjectId(n);
        }
        return;
      }
      if (selectedProjectId == null) {
        const firstId = projectsForDropdown[0].value;
        const n = parseInt(String(firstId), 10);
        if (!Number.isNaN(n)) {
          setValue('project', n);
          setSelectedProjectId(n);
        }
      } else {
        setValue('project', selectedProjectId);
      }
      return;
    }
    if (routeId) {
      const p = parseInt(String(routeId), 10);
      setValue('project', p);
      setSelectedProjectId(p);
    } else if (selectedProjectId == null) {
      const firstId = projectsForDropdown[0].value;
      const n = parseInt(String(firstId), 10);
      setValue('project', n);
      setSelectedProjectId(n);
      navigate(`/builder/sales/unit-inventory/${n}`, { replace: true });
    } else {
      setValue('project', selectedProjectId);
    }
  }, [projectsForDropdown, routeId, selectedProjectId, setValue, navigate, embedded, forcedProjectID]);

  useEffect(() => {
    if (projectFieldValue === undefined || projectFieldValue === '' || projectFieldValue === null) {
      return;
    }
    const id = parseInt(String(projectFieldValue), 10);
    if (Number.isNaN(id) || id < 1) return;
    if (id === selectedProjectId) return;
    setSelectedProjectId(id);
    if (!embedded) {
      navigate(`/builder/sales/unit-inventory/${id}`);
    }
  }, [projectFieldValue, selectedProjectId, navigate, embedded]);

  useEffect(() => {
    if (!effectiveProjectId) return;
    setValue('block', '');
  }, [effectiveProjectId, setValue]);

  useEffect(() => {
    if (blockOptions.length === 0) return;
    const currentInOptions = blockOptions.some((o) => String(o.value) === String(blockFieldValue));
    if (!blockFieldValue || !currentInOptions) {
      setValue('block', blockOptions[0].value);
    }
  }, [blockOptions, blockFieldValue, setValue]);

  const filterRowClass = embedded
    ? `fixed top-16 z-[9] border-b border-gray-200 transition-all duration-300 ease-in-out bg-white ${
        collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
      }`
    : `flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm z-[10] transition-all duration-300 ease-in-out ${
        collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
      }`;

  const filterForm = (
    <div className={filterRowClass}>
      <div
        className={`flex w-full items-center justify-between gap-4 ${
          embedded ? 'flex-nowrap px-6 py-3' : 'flex-nowrap px-6 py-4'
        }`}>
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <div className="w-72 max-w-[min(18rem,calc(100vw-8rem))] shrink-0">
            <FormInput
              width={100}
              label={''}
              register={register}
              register_key={'project'}
              isRequired={true}
              type={'dropdown'}
              options={[
                { label: 'Select Project', value: '', disabled: true },
                ...(projectsForDropdown || [])
              ]}
              errors={errors}
              hideAstrik={true}
              mb
            />
          </div>
          <div className="w-56 max-w-[min(14rem,calc(100vw-10rem))] shrink-0">
            <FormInput
              width={100}
              label={''}
              register={register}
              register_key={'block'}
              isRequired={false}
              type={'dropdown'}
              options={[{ label: 'Select Block', value: '', disabled: true }, ...blockOptions]}
              errors={errors}
              hideAstrik={true}
              mb
            />
          </div>
        </div>
        <div ref={bulkUpdatePortalRef} className="ml-auto shrink-0" />
      </div>
    </div>
  );

  if (!routeId && !embedded) {
    return (
      <>
        <Loader isVisible={isPageLoading} />
        <Layout>
          <div
            className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
              ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
            `}
          >
            <Header data={{ name: 'Sales > Unit Inventory' }} />
          </div>
          <div
            className={`flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] border-t-[2px] border-stone-100 shadow-sm z-[10] transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'}`}
          >
            <div className="flex items-center gap-4 w-1/3">
              <FormInput
                width={100}
                label={''}
                register={register}
                register_key={'project'}
                isRequired={true}
                type={'dropdown'}
                options={[
                  { label: 'Select Project', value: '', disabled: true },
                  ...(projectsForDropdown || [])
                ]}
                errors={errors}
                hideAstrik={true}
                mb
              />
            </div>
          </div>
          <div className="mt-[9rem] h-[calc(100vh-9.5rem)] overflow-auto bg-gray-100 flex flex-col items-center justify-center">
            <h2 className="text-lg font-bold text-gray-800 mb-1">Select a project</h2>
            <p className="text-gray-600 text-sm">Choose a project to view unit pricing and inventory.</p>
          </div>
        </Layout>
      </>
    );
  }

  if (embedded) {
    return (
      <>
        <Loader isVisible={isPageLoading} />
        {filterForm}
        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          <div className="pt-32 min-h-[calc(100vh-8rem)] overflow-auto bg-gray-100">
            {Number.isFinite(effectiveProjectId) && effectiveProjectId > 0 && (
              <SalesUnitInventoryList
                projectId={effectiveProjectId}
                selectedBlockId={blockFieldValue}
                onBlockChange={(blockId) => setValue('block', blockId)}
                blocksFromParent={blockOptions}
                openBulkUnitUpdate={openBulkUnitUpdate}
                bulkUpdatePortalEl={bulkUpdatePortalEl}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <Layout>
      <Loader isVisible={isPageLoading} />
      <div
        className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white
          ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'}
        `}
      >
        <Header data={{ name: 'Sales > Unit Inventory' }} />
      </div>
      {filterForm}
      <div className="mt-[9rem] min-h-[calc(100vh-9.5rem)] overflow-auto bg-gray-100">
        {effectiveProjectId ? (
          <SalesUnitInventoryList
            projectId={effectiveProjectId}
            selectedBlockId={blockFieldValue}
            onBlockChange={(blockId) => setValue('block', blockId)}
            blocksFromParent={blockOptions}
            openBulkUnitUpdate={openBulkUnitUpdate}
            bulkUpdatePortalEl={bulkUpdatePortalEl}
          />
        ) : null}
      </div>
    </Layout>
  );
};

export default SalesUnitInventory;
